import { ca } from 'date-fns/locale';
import TPEAction from '../../models/common/TPEAction';
import PLIConfigState from './PLIConfigState';
import { PLIConfig, PLIConfigStep, PLIConfigTemplateSection } from 'src/models/calculation-builder/pli-config/PLIConfigTemplate';
import { SavingStatus } from 'src/models/common/SavingStatus';
import CONSTANTS from 'src/utils/constants';
import StringUtils from 'src/utils/stringUtils';
import PLIConfigService from './PLIConfigService';
import ValidatePLIConfigResponse from 'src/models/calculation-builder/pli-config/ValidatePLIConfigResponse';
import { CalcBuilderComment } from 'src/models/calculation-builder/CalcBuilderComment';

/**
 * List here the actions supported by this reducer
 */
export const ACTIONS = Object.freeze({
    SET_PLI_TEMPLATES: new TPEAction('SET_PLI_TEMPLATES'),
    SET_SELECTED_PLI_TEMPLATE: new TPEAction('SET_SELECTED_PLI_TEMPLATE'),
    SET_PLI_CONFIG_METADATA: new TPEAction('SET_PLI_CONFIG_METADATA'),
    SET_PLI_CONFIG: new TPEAction('SET_PLI_CONFIG'),
    SET_STEP_BEING_EDITED: new TPEAction('SET_STEP_BEING_EDITED'),
    SET_SECTION_BEING_EDITED: new TPEAction('SET_SECTION_BEING_EDITED'),
    UPDATE_CURRENT_STEP: new TPEAction('UPDATE_CURRENT_STEP'),
    REFRESH_STEP: new TPEAction('REFRESH_STEP'),
    ADD_SECTION_STEP: new TPEAction('ADD_SECTION_STEP'),
    ADD_SECTION_STEP_BEFORE: new TPEAction('ADD_SECTION_STEP_BEFORE'),
    ADD_SECTION_STEP_AFTER: new TPEAction('ADD_SECTION_STEP_AFTER'),
    DUPLICATE_SECTION_STEP: new TPEAction('DUPLICATE_SECTION_STEP'),
    REMOVE_SECTION_STEP: new TPEAction('REMOVE_SECTION_STEP'),
    SAVE_ALL_SECTIONS: new TPEAction('SAVE_ALL_SECTIONS'),
    SET_REMOVE_STEP_PAYLOAD: new TPEAction('SET_REMOVE_STEP_PAYLOAD'),
    SET_VALIDATION_RESULTS: new TPEAction('SET_VALIDATION_RESULTS'),
    SET_EDITOR_CARET_POSITION: new TPEAction('SET_EDITOR_CARET_POSITION'),
    APPEND_TO_FORMULA_EDITOR: new TPEAction('APPEND_TO_FORMULA_EDITOR'),
    UPDATE_SETPS_WITH_RUN_RESULTS: new TPEAction('UPDATE_SETPS_WITH_RUN_RESULTS'),
    SET_STEP_COMMENTS: new TPEAction('SET_STEP_COMMENTS'),
    SET_NEW_COMMENT_MODAL_PAYLOAD: new TPEAction('SET_NEW_COMMENT_MODAL_PAYLOAD'),
    SET_ADD_COMMENT_PAYLOAD: new TPEAction('SET_ADD_COMMENT_PAYLOAD'),
    ADD_STEP_COMMENT: new TPEAction('ADD_STEP_COMMENT'),
    SET_COMMENTS_HISTORY: new TPEAction('SET_COMMENTS_HISTORY'),
    CLEAR_UNSAVED_COMMENTS: new TPEAction('CLEAR_UNSAVED_COMMENTS'),
    TOGGLE_SHOW_VALUES: new TPEAction('TOGGLE_SHOW_VALUES'),
});

const VALIDATION_MESSAGES = Object.freeze({
    DESCRIPTION_REQUIRED: "Step name is required!",
    OVERVIEW_REQUIRED: "Step overview is required!",
});

/**  
* This function is responsible for updating the state based on action type
* @param state The current dashboard state
* @param action The current dispatched action 
*/
export function pliConfigReducer(state: PLIConfigState, action: TPEAction): PLIConfigState {
    switch (action.type) {
        case ACTIONS.SET_PLI_TEMPLATES.type: {
            return {
                ...state,
                pliTemplates: action.payload,
            }
        }
        case ACTIONS.SET_SELECTED_PLI_TEMPLATE.type: {
            return {
                ...state,
                selectedPLITemplate: action.payload,
            }
        }
        case ACTIONS.SET_PLI_CONFIG_METADATA.type: {
            return {
                ...state,
                pliConfigMetadata: action.payload,
            }
        }
        case ACTIONS.SET_PLI_CONFIG.type: {
            const { sections, isValid } = action.payload as PLIConfig
            return {
                ...state,
                pliConfig: action.payload,
                templateSections: sections,
                allSectionsAreSaved: true,
                allSectionsAreValid: isValid,
            }
        }
        case ACTIONS.SET_STEP_BEING_EDITED.type: {
            const { sectionBeingEdited, templateSections } = state;
            const { step, section } = action.payload as {step: PLIConfigStep, section: PLIConfigTemplateSection};
            
            if (step == null && section != null){
                section.steps.forEach(x => x.isBeingEdited = false);
                return {
                    ...state,
                    stepBeingEdited: undefined,
                    sectionBeingEdited: {...section},
                };
            }

            if (sectionBeingEdited == null){
                state.sectionBeingEdited = section
            }
            if (section == null){
                return state;
            }
            
            const allSteps = section.steps;
            allSteps.forEach(x => x.isBeingEdited = false);
            
            const editingRecordIndex = section.steps.findIndex((x:any) => x.stepId === step.stepId);
            const editingRecord = section.steps[editingRecordIndex];
            if (editingRecord){
                editingRecord.isBeingEdited = true;
                section.steps = [...section.steps];
            }

            return {
                ...state,
                stepBeingEdited: {...editingRecord},
                templateSections: [...templateSections],
                sectionBeingEdited: {...section}
            };
        }
        case ACTIONS.UPDATE_CURRENT_STEP.type: {
            const { templateSections } = state;
            const { field, value, extraValue } = action.payload;
            if (state.stepBeingEdited == null) {
                return {
                    ...state,
                    error: `No PLI step is being edited. Step's ${action.payload.field} could not be updated.`,
                    updateStepPayload: null
                };
            }
            const step = (state.stepBeingEdited as any);
            if (step){
                const oldValue = step[field];
                const newValue = value;
                
                if (oldValue !== newValue){
                    step.savingStatus = SavingStatus.Unsaved;
                }

                step[field] = newValue;
                if (field === CONSTANTS.PLI_CONFIG_STEP_FIELDS.OVERVIEW) {
                    step[CONSTANTS.PLI_CONFIG_STEP_FIELDS.EXPRESSION_TOKENS] = extraValue;
                }
                validateStep(step);
                const section = templateSections.find(s => s.name === step.sectionName);
                if ( section != null ){
                    const stepIndex = (section.steps || []).findIndex(f => f.stepId === step.stepId);
                    if (stepIndex >= 0 ) {
                        section.steps[stepIndex] = step;
                    }
                }
                if ( step.validationErrors.length > 0 ) {
                    return {
                        ...state,
                        error: '',
                        templateSections: [...state.templateSections],
                        allSectionsAreSaved: false,
                        allSectionsAreValid: false,
                        updateStepPayload: null
                    };
                }

                return {
                    ...state,
                    error: '',
                    templateSections: [...state.templateSections],
                    allSectionsAreSaved: false,
                    allSectionsAreValid: false,
                    updateStepPayload: step.savingStatus === SavingStatus.Unsaved? {...step} : null,
                }
            }
            return {
                ...state,
                updateStepPayload: null         
            }
        }
        case ACTIONS.REFRESH_STEP.type: {
            const { templateSections, stepBeingEdited } = state;
            const section = templateSections.find(x => x.name === action.payload.sectionName);
            if (section == null){
                return {
                    ...state,
                    error: 'Could not refresh step because section was not found',
                    allSectionsAreSaved: false,
                    allSectionsAreValid: false,
                }
            }
            const stepIndex = section.steps.findIndex((x:any) => x.stepId === action.payload.stepId || x.stepId === action.payload.unsavedId);
            if (stepIndex >= 0){
                const stepToBeRefreshed = section.steps[stepIndex];
                stepToBeRefreshed.stepId = action.payload.stepId;
                stepToBeRefreshed.valid = action.payload.valid;
                stepToBeRefreshed.expression = action.payload.expression;
                stepToBeRefreshed.isNew = false;
                stepToBeRefreshed.validationErrors = action.payload.validationErrors;
                stepToBeRefreshed.savingStatus = SavingStatus.Saved;
                stepToBeRefreshed.isBeingEdited = false;
            }
            else {
                console.error("********* Could not refresh step because step was not found");
            }
            return {
                ...state,
                error: '',
                templateSections: [...templateSections],
                //stepSavedId: stepToBeRefreshed?.stepId,
            }
        }
        case ACTIONS.ADD_SECTION_STEP.type: {
            const { templateSections } = state;
            const { section, newStep } = action.payload;
            const stepsList = (section.steps || []) as PLIConfigStep[];
            stepsList.forEach(x => x.isBeingEdited = false);
            stepsList.push(newStep);
            section.steps = [...stepsList];
            return {
                ...state,
                allSectionsAreSaved: false,
                allSectionsAreValid: false,
                error: '',
                templateSections: [...templateSections],
                stepBeingEdited: newStep
            }
        }
        case ACTIONS.ADD_SECTION_STEP_BEFORE.type: {
            const { templateSections } = state;
            const { index, newStep, section } = action.payload;
            const stepsList = (section.steps || []) as PLIConfigStep[];
            newStep.id = `${stepsList.length + 1}`;
            stepsList.forEach(x => x.isBeingEdited = false);
            
            // Inserting new item
            stepsList.splice(index === 0? 0 : index, 0, newStep);
            for (let i = index + 1; i < stepsList.length; i++) {
                const step = stepsList[i];
                step.sequence++;
                step.savingStatus = SavingStatus.Unsaved;
            }
            if (section != null){
                section.steps = [...stepsList];
            }
            return {
                ...state,
                allSectionsAreSaved: false,
                allSectionsAreValid: false,
                error: '',
                templateSections: [...templateSections],
                stepBeingEdited: newStep
            }
        }
        case ACTIONS.ADD_SECTION_STEP_AFTER.type: {
            const { templateSections } = state;
            let { index, newStep, section } = action.payload;
            const stepsList = (section.steps || []) as PLIConfigStep[];
            newStep.id = `${stepsList.length + 1}`;
            stepsList.forEach(x => x.isBeingEdited = false);
            
            // Inserting new item
            stepsList.splice(index >= stepsList.length - 1? stepsList.length : index + 1, 0, newStep);
            for (let i = index + 1; i < stepsList.length; i++) {
                const step = stepsList[i];
                step.sequence++;
                step.savingStatus = SavingStatus.Unsaved;
            }
            if (section != null){
                section.steps = stepsList;
            }
            return {
                ...state,
                allSectionsAreSaved: false,
                allSectionsAreValid: false,
                error: '',
                templateSections: [...templateSections],
                sectionBeingEdited: section,
                stepBeingEdited: newStep
            }
        }
        case ACTIONS.DUPLICATE_SECTION_STEP.type: {
            const { templateSections } = state;
            const { originalStep, section } = action.payload as {originalStep: PLIConfigStep, section: PLIConfigTemplateSection};
            const nameCopiesCount = section.steps.filter(x => x.description.endsWith(originalStep.description)).length;
            const copyPrefix = `DUPLICATE${nameCopiesCount === 1? '' : '('+nameCopiesCount+')'} - `;
            const copyName = `${copyPrefix}${originalStep.description}`;
            const stepsList = section?.steps || [];
            const stepCopy = {...originalStep, 
                description: copyName, 
                stepId: `${templateSections.length + 1}`, 
                savingStatus: SavingStatus.Unsaved, 
                isNew: true,
                sequence: stepsList.length + 1,
                expression: originalStep.expression
            } as PLIConfigStep;
            
            // Inserting copy
            stepsList.push(stepCopy);
            
            if (section != null){
                section.steps = [...stepsList];
            }
            return {
                ...state,
                error: '',
                allSectionsAreSaved: false,
                allSectionsAreValid: false,
                templateSections: [...templateSections],
                updateStepPayload: stepCopy
            }
        }
        case ACTIONS.REMOVE_SECTION_STEP.type: {
            const { templateSections, sectionBeingEdited } = state;
            const { index, step } = action.payload;
            if (step.isNew) {
                let stepsList = sectionBeingEdited?.steps || [];
                for (let i = index; i < stepsList.length; i++) {
                    const s = stepsList[i];
                    s.sequence--;
                    s.savingStatus = SavingStatus.Unsaved;
                }
                stepsList = stepsList.filter(x => x.stepId !== step.stepId);
                if (sectionBeingEdited != null){
                    sectionBeingEdited.steps = stepsList;
                }
                return {
                    ...state,
                    error: '',
                    removeStepModalPayload: null,
                    allSectionsAreSaved: false,
                    allSectionsAreValid: false,
                    templateSections: [...templateSections],
                }
            }
            return {
                ...state,
                error: '',
                allSectionsAreSaved: false,
                allSectionsAreValid: false,
                deleteStepPayload: {calculationNumber:step.calculationNumber, stepId: step.stepId},
            }
        }
        case ACTIONS.SAVE_ALL_SECTIONS.type: {
            const { templateSections, stepsThatNeedSaving } = state;
            let unsavedSteps = stepsThatNeedSaving;
            // If there are no unsaved steps we look for them
            if (unsavedSteps.length === 0) {
                unsavedSteps = PLIConfigService.flattenSectionSteps(templateSections).filter(x => x.savingStatus === SavingStatus.Unsaved);
                if (unsavedSteps.length === 0){
                    templateSections.forEach(s => s.steps.forEach(x => x.isBeingEdited = false));
                    return {
                        ...state,
                        error: '',
                        templateSections: [...state.templateSections],
                        allSectionsAreSaved: true,
                        allSectionsAreValid: false,
                        stepsThatNeedSaving: [],
                        stepBeingEdited: undefined,
                        sectionBeingEdited:undefined,
                    }
                }
            }
            // Check for validation on all steps skip until we find one that can be saved
            for (let i = unsavedSteps.length - 1; i >= 0; i--) {
                const unsavedStep = unsavedSteps[i];
                validateStep(unsavedStep);
                const section = templateSections.find(s => s.name === unsavedStep.sectionName);
                if ( section != null ){
                    const stepIndex = (section.steps || []).findIndex(f => f.stepId === unsavedStep.stepId);
                    if (stepIndex >= 0 ) {
                        section.steps[stepIndex] = unsavedStep;
                    }
                }
                if ( unsavedStep.validationErrors?.length ) {
                    continue;
                }
                return {
                    ...state,
                    error: '',
                    templateSections: [...state.templateSections],
                    allSectionsAreSaved: false,
                    allSectionsAreValid: false,
                    updateStepPayload: {...unsavedStep},
                    stepsThatNeedSaving: [...unsavedSteps.slice(0, i), ...unsavedSteps.slice(i + 1)]
                }
            }

            if (unsavedSteps.length === 0){
                templateSections.forEach(s => s.steps.forEach(x => x.isBeingEdited = false));
                return {
                    ...state,
                    templateSections: [...state.templateSections],
                    stepBeingEdited: undefined,
                    sectionBeingEdited:undefined,
                    allSectionsAreSaved: true,
                    allSectionsAreValid: false
                };
            }
            

            return {
                ...state,
                error: '',
                templateSections: [...state.templateSections],
                allSectionsAreSaved: false,
                allSectionsAreValid: false,
            }
        }
        case ACTIONS.SET_REMOVE_STEP_PAYLOAD.type: {
            if (action.payload){
                const {step} = action.payload;
                if (step.isNew){
                    const section = state.templateSections.find(x => x.name === step.sectionName) || {steps:[]};
                    section.steps = section?.steps.filter(x => x.stepId !== step.stepId) || [];
                    return {
                        ...state,
                        error: '',
                        templateSections: [...state.templateSections],
                    }
                }
            }
            return {
                ...state,
                error: '',
                removeStepModalPayload: action.payload,
            }
        }
        case ACTIONS.SET_VALIDATION_RESULTS.type: {
            const validationResult = action.payload as ValidatePLIConfigResponse
            if (action.payload){
                const {step} = action.payload;
                if (step.isNew){
                    const section = state.templateSections.find(x => x.name === step.sectionName) || {steps:[]};
                    section.steps = section?.steps.filter(x => x.stepId !== step.stepId) || [];
                    return {
                        ...state,
                        error: '',
                        templateSections: [...state.templateSections],
                    }
                }
            }
            return {
                ...state,
                error: '',
                removeStepModalPayload: action.payload,
            }
        }
        case ACTIONS.SET_EDITOR_CARET_POSITION.type: {
            const { stepBeingEdited, sectionBeingEdited, templateSections } = state;
            if (stepBeingEdited == null) {
                return {
                    ...state,
                    error: `No calculation step is being edited. The value '${action.payload}' could not be set as caret position.`,
                };
            }
            stepBeingEdited.formulaEditorCaretPosition = action.payload;
            return {
                ...state,
                error: '',
                stepBeingEdited: {...stepBeingEdited},
            }
        }                
        case ACTIONS.APPEND_TO_FORMULA_EDITOR.type: {
            const { stepBeingEdited, sectionBeingEdited, templateSections } = state;
            if (stepBeingEdited == null) {
                if (sectionBeingEdited != null) {
                    sectionBeingEdited.error = `No PLI step is being edited. The value '${action.payload}' could not be added to the formula editor.`
                }
                return {
                    ...state,
                    sectionBeingEdited
                };
            }
            const section = templateSections.find(x => x.name === sectionBeingEdited?.name);
            const step = stepBeingEdited;
            if (step == null || section == null){
                return state;
            }
            const caretPosition = step.formulaEditorCaretPosition || step.expression.length;
            const paddingBeforePosition = step.expression.charAt(caretPosition - 1) === ' '? '' : ' ';
            const paddingAfterPosition = step.expression.charAt(caretPosition) === ' '? '' : ' ';
            const str = `${paddingBeforePosition}${action.payload}${paddingAfterPosition}`;
            step.expression = [step.expression.slice(0, caretPosition), str, step.expression.slice(caretPosition)].join('');
            
            const stepsList = section?.steps || [];
            stepsList[stepsList.findIndex(x => x.stepId === step.stepId)] = step;
            section.steps = [...stepsList];
            
            return {
                ...state,
                error: '',
                templateSections: [...templateSections],
                stepBeingEdited: {...step},
                sectionBeingEdited: {...section}
            }
        }
        case ACTIONS.UPDATE_SETPS_WITH_RUN_RESULTS.type: {
            const { templateSections } = state;
            const stepsWithValues = action.payload as PLIConfigStep [];
            const allSteps = PLIConfigService.flattenSectionSteps(templateSections);
            stepsWithValues.forEach(s => {
                const step = allSteps.find(x => x.stepId === s.stepId);
                if (step){
                    step.formulaOutput = s.value;
                    step.valueBreakdown = s.valueBreakdown;
                }
            })
            templateSections.forEach(x => {
                x.steps.forEach(x => x.isBeingEdited = false)
                x.steps = [...x.steps]
            })
            return {
                ...state,
                templateSections: [...templateSections],
                stepBeingEdited: undefined,
                sectionBeingEdited: undefined,
                allSectionsAreValid: true
            }
        }
        case ACTIONS.SET_STEP_COMMENTS.type: {
            const { templateSections, commentsModalPayload } = state;
            const allSteps = PLIConfigService.flattenSectionSteps(templateSections);
            const step = allSteps.find(x => x.stepId === commentsModalPayload.step.stepId);
            if (step != null) {
                const comments = action.payload as CalcBuilderComment[];
                commentsModalPayload.step.comments = step.comments = comments;
                step.hasComments = commentsModalPayload.step.hasComments = comments.length > 0;
            }
            return {
                ...state,
                error: '',
                templateSections: [...templateSections],
            }
        }
        case ACTIONS.SET_NEW_COMMENT_MODAL_PAYLOAD.type: {
            if (action.payload == null){
                return {
                    ...state,
                    commentsModalPayload: action.payload,
                    commentsNeedRefresh: false,
                }
            }
            const {unsavedComments} = state;
            const isNewStep = action.payload?.step?.isNew || false;
            return {
                ...state,
                error: '',
                commentsModalPayload: action.payload,
                commentsNeedRefresh: !isNewStep,
                commentsHistory: isNewStep? [...unsavedComments] : []                
            }
        }
        case ACTIONS.SET_STEP_COMMENTS.type:
            const { templateSections, commentsModalPayload } = state;
            const allSteps = PLIConfigService.flattenSectionSteps(templateSections);
            const step = allSteps.find(x => x.stepId === commentsModalPayload.step.stepId);
            if (step != null) {
                const comments = action.payload as CalcBuilderComment[];
                commentsModalPayload.step.comments = step.comments = comments;
                step.hasComments = commentsModalPayload.step.hasComments = comments.length > 0;
            }
            return {
                ...state,
                error: '',
                templateSections: [...templateSections],
            }
        case ACTIONS.ADD_STEP_COMMENT.type: {
            const comment: CalcBuilderComment = action.payload;
            const { commentsHistory, unsavedComments, commentsModalPayload, templateSections } = state;
            commentsHistory.splice(0, 0, comment);
            // If step is not saved yet we add the comment to the unsaved list and attempt to turn on the hasComments flag on the step
            const allSteps = PLIConfigService.flattenSectionSteps(templateSections);
            const found = allSteps.find(x => x.stepId === commentsModalPayload.step.stepId);
            if (comment.ownerIsUnsaved) {
                unsavedComments.splice(0, 0, comment);
                if ( commentsModalPayload.step ){
                    if (found){
                        found.hasComments = true;
                    }
                }
            }
            if (found){
                found.hasComments = true;
                templateSections.forEach(x => {
                    x.steps = [...x.steps]
                })
            }
            return {
                ...state,
                commentsNeedRefresh: !comment.ownerIsUnsaved,
                templateSections: [...templateSections],
            }
        }
        case ACTIONS.SET_ADD_COMMENT_PAYLOAD.type: {
            return {
                ...state,
                error: '',
                addCommentPayload: action.payload,
            }
        }
        case ACTIONS.SET_COMMENTS_HISTORY.type: {
            return {
                ...state,
                commentsHistory: action.payload,
            }
        }
        case ACTIONS.CLEAR_UNSAVED_COMMENTS.type: {
            return {
                ...state,
                commentsHistory: [],
                unsavedComments: [],
            }
        }
        case ACTIONS.TOGGLE_SHOW_VALUES.type: {
            const { templateSections } = state;
            const sectionFound = templateSections.find(x => x.name === action.payload.name);
            if (sectionFound){
                sectionFound.showValues = !sectionFound.showValues;
            }
            return {
                ...state,
                error: '',
                templateSections: [...templateSections],
            }
        }
    }
    return state;
}

function validateStep(step: any) {
    step.validationErrors = [];
    if (StringUtils.isNullOrEmpty(step.description)){
        step.validationErrors.push({message:VALIDATION_MESSAGES.DESCRIPTION_REQUIRED, stepId: step.stepId})
    }
    if (StringUtils.isNullOrEmpty(step.expression)){
        step.validationErrors.push({message:VALIDATION_MESSAGES.OVERVIEW_REQUIRED, stepId: step.stepId})
    }
}
